import { IClientAllocationDetailed } from '~/services/api/clientAllocation/types';
import { ITeamMemberMapped } from '~/utils/clientAllocation';

import { ClientWithAllocation, NormalizedTeamMembersAllocations } from './types';

const createTeamMemberEntry = (
  teamMember: ITeamMemberMapped,
): NormalizedTeamMembersAllocations => ({
  id: teamMember.id,
  name: teamMember.fullName,
  photo: teamMember.photo ?? '',
  roles: teamMember.roles ?? [],
  clientsAllocations: [],
  crisisAllocations: [],
  selectedClientsIds: [],
  locationGroupAllocations: [],
  selectedLocationGroupIds: [],
});

const normalizeAllocation = (allocation: IClientAllocationDetailed): ClientWithAllocation[] => {
  const { user, ...allocationData } = allocation;

  return user.clients.map(client => ({
    memo: client.memo || '',
    userId: user.id,
    teams: [allocationData.team],
    team: allocationData.team,
    teamId: allocationData.team?.id || null,
    id: client.id,
    name: `${client.firstName} ${client.lastName}`,
    clientId: client.id,
    allocationId: allocationData.id,
    photo: client.photo,
    allocation: allocationData,
  }));
};

const normalizeAllocationDuplicate = (
  allocation: IClientAllocationDetailed,
  actTeamId?: number,
): ClientWithAllocation[] => {
  const { user, ...allocationData } = allocation;

  return user.clients.map(
    (client): ClientWithAllocation => ({
      memo: client.memo || '',
      userId: user.id,
      teams: [allocationData.team],
      team: allocationData.team,
      teamId: allocationData.team?.id || actTeamId || null,
      id: client.id,
      name: `${client.firstName} ${client.lastName}`,
      clientId: client.id,
      photo: client.photo,
      allocationId: null,
      allocation: null,
    }),
  );
};

export const normalizeDuplicateAllocationsByUser = (
  allocations: IClientAllocationDetailed[],
  teamMembers: ITeamMemberMapped[],
  actTeamId?: number,
): NormalizedTeamMembersAllocations[] => {
  const membersMap = new Map<number, NormalizedTeamMembersAllocations>();

  teamMembers.forEach(teamMember => {
    membersMap.set(teamMember.id, createTeamMemberEntry(teamMember));
  });

  allocations.forEach(allocation => {
    const member = membersMap.get(allocation.user.id);
    // user cannot duplicate crisis allocations accordingly to requirements
    if (!member || allocation.isCrisis) {
      return;
    }

    const normalizedAllocation = normalizeAllocationDuplicate(allocation, actTeamId);

    if (allocation.locationGroups.length) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { user } = allocation;
      const clientMemos = Object.fromEntries(user.clients.map(c => [c.id, c.memo ?? ''])) as Record<
        number,
        string
      >;
      allocation.locationGroups.forEach(lg => {
        member.locationGroupAllocations.push({
          locationGroupId: lg.id,
          locationGroupName: lg.name,
          // Duplicate - we create new allocation
          allocation: null,
          clientMemos,
          removedClientIds: [],
        });
        member.selectedLocationGroupIds.push(lg.id);
      });
      return;
    }

    member.selectedClientsIds.push(...normalizedAllocation.map(client => client.id));
    member.clientsAllocations.push(...normalizedAllocation);
  });

  return teamMembers
    .map(teamMember => membersMap.get(teamMember.id))
    .filter((member): member is NormalizedTeamMembersAllocations => member != null);
};

const normalizeAllocationsByUser = (
  allocations: IClientAllocationDetailed[],
  teamMembers: ITeamMemberMapped[],
): NormalizedTeamMembersAllocations[] => {
  const membersMap = new Map<number, NormalizedTeamMembersAllocations>();

  teamMembers.forEach(teamMember => {
    membersMap.set(teamMember.id, createTeamMemberEntry(teamMember));
  });

  allocations.forEach(allocation => {
    const member = membersMap.get(allocation.user.id);

    if (!member || allocation.isArchived) {
      return;
    }

    const normalizedAllocation = normalizeAllocation(allocation);

    if (allocation.isCrisis) {
      member.crisisAllocations.push(...normalizedAllocation);
      return;
    }

    if (allocation.locationGroups.length) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { user, ...allocationData } = allocation;
      const clientMemos = Object.fromEntries(user.clients.map(c => [c.id, c.memo ?? ''])) as Record<
        number,
        string
      >;
      allocation.locationGroups.forEach(lg => {
        member.locationGroupAllocations.push({
          locationGroupId: lg.id,
          locationGroupName: lg.name,
          allocation: allocationData,
          clientMemos,
          removedClientIds: [],
        });
        member.selectedLocationGroupIds.push(lg.id);
      });
      return;
    }

    member.selectedClientsIds.push(...normalizedAllocation.map(client => client.id));
    member.clientsAllocations.push(...normalizedAllocation);
  });

  return teamMembers
    .map(teamMember => membersMap.get(teamMember.id))
    .filter((member): member is NormalizedTeamMembersAllocations => member != null);
};

export default normalizeAllocationsByUser;
